import type { AsyncData } from '#app';
import type { Globals } from '~/types';

/**
 * Fetches globals at runtime through the Directus proxy.
 *
 * In a multi-tenant Directus setup, readItems + proxy params (cuid/ne)
 * resolves the correct tenant record more reliably than build-time singleton reads.
 */
export const useGlobals = (): AsyncData<Globals | null, Error | null> => {
  return useAsyncData<Globals | null>(
    'globals',
    async () => {
      try {
        const data = await useDirectusProxy<Globals[]>({
          operation: 'readItems',
          collection: 'globals',
          params: {
            fields: [
              'id',
              'title',
              'tagline',
              'description',
              'url',
              'email',
              'phone',
              'street_address',
              'address_locality',
              'address_region',
              'address_country',
              'postal_code',
              'og_image',
              'logo_on_light_bg',
              'logo_on_dark_bg',
              'social_links',
            ],
            limit: 1,
          },
        });

        if (!Array.isArray(data) || data.length === 0) {
          if (process.env.LOG_LEVEL === 'debug') {
            console.warn('[useGlobals] No globals rows returned from proxy');
          }
          return null;
        }

        const globals = data[0] as Globals;

        if (process.env.LOG_LEVEL === 'debug') {
          console.debug('[useGlobals] Loaded globals id:', globals?.id ?? '(none)');
          console.debug('[useGlobals] logo_on_light_bg:', String(globals?.logo_on_light_bg ?? '(empty)'));
          console.debug('[useGlobals] logo_on_dark_bg:', String(globals?.logo_on_dark_bg ?? '(empty)'));
        }

        return globals;
      } catch (error) {
        console.error('[useGlobals] Failed to fetch globals:', error);
        return null;
      }
    },
    {
      default: () => null,
    },
  ) as AsyncData<Globals | null, Error | null>;
};
